'use client';


/** Imports */
import {useTranslations} from 'next-intl';
import Dialog from '@/components/Dialog/Dialog';
import Loading from '@/components/Loading/Loading';
import React, {createContext, useCallback, useMemo} from 'react';


/** Types */
type Props = Readonly<{
    children: React.ReactNode;
}>;


/** Constants */
export const HelperContext = createContext<{
    openLoadingDialog: () => void;
    closeLoadingDialog: () => void;
    openSaveDialog: () => void;
    closeSaveDialog: () => void;
}>(null as any);


/** Component */
export function HelperProvider(props: Props) {
    const {children} = props;

    // Hooks
    const t = useTranslations('common');

    // Refs
    const loadingDialogRef = React.useRef<HTMLDialogElement>(null);
    const saveDialogRef = React.useRef<HTMLDialogElement>(null);
    const loadingDialogOpenCount = React.useRef(0);
    const saveDialogOpenCount = React.useRef(0);

    // Event handlers
    const openLoadingDialog = useCallback(() => {
        loadingDialogOpenCount.current += 1;
        if (!loadingDialogRef.current?.open) {
            loadingDialogRef.current?.showModal();
        }
    }, []);
    const closeLoadingDialog = useCallback(() => {
        loadingDialogOpenCount.current = Math.max(loadingDialogOpenCount.current - 1, 0);
        if ((loadingDialogOpenCount.current === 0) && loadingDialogRef.current?.open) {
            loadingDialogRef.current.close();
        }
    }, []);
    const openSaveDialog = useCallback(() => {
        saveDialogOpenCount.current += 1;
        if (!saveDialogRef.current?.open) {
            saveDialogRef.current?.showModal();
        }
    }, []);
    const closeSaveDialog = useCallback(() => {
        saveDialogOpenCount.current = Math.max(saveDialogOpenCount.current - 1, 0);
        if ((saveDialogOpenCount.current === 0) && saveDialogRef.current?.open) {
            saveDialogRef.current.close();
        }
    }, []);

    // Memos
    const contextValue = useMemo(() => ({
        openLoadingDialog, closeLoadingDialog, openSaveDialog, closeSaveDialog,
    }), [openLoadingDialog, closeLoadingDialog, openSaveDialog, closeSaveDialog]);


    // Render
    return (
        <HelperContext.Provider
            value={contextValue}
        >

            {/* Loading dialog */}
            <Dialog
                reference={loadingDialogRef}
                headline={t('loading')}
                disableCloseOnBackdrop={true}
                noCloseButton={true}
            >
                <Loading/>
            </Dialog>

            {/* Save dialog */}
            <Dialog
                reference={saveDialogRef}
                headline={t('saving')}
                disableCloseOnBackdrop={true}
                noCloseButton={true}
            >
                <Loading/>
            </Dialog>

            {/* Content */}
            {children}
        </HelperContext.Provider>
    );
}
